'use client';

import { Message } from '@/types/chat';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

interface MessageBubbleProps {
    message: Message;
    isOwn: boolean;
}

export default function MessageBubble({ message, isOwn }: MessageBubbleProps) {
    if (message.type === 'system') {
        return (
            <div className="flex justify-center my-2">
                <p className="text-xs text-muted-foreground italic bg-muted px-3 py-1 rounded-full">
                    {message.content}
                </p>
            </div>
        );
    }

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'admin':
                return 'bg-red-500';
            case 'organizer':
                return 'bg-purple-500';
            case 'participant':
                return 'bg-blue-500';
            default:
                return 'bg-gray-500';
        }
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const formatTime = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className={cn('flex gap-2 mb-3', isOwn && 'flex-row-reverse')}>
            {/* Avatar */}
            {!isOwn && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="text-xs bg-primary/10">
                        {getInitials(message.userName)}
                    </AvatarFallback>
                </Avatar>
            )}

            {/* Message Content */}
            <div className={cn('flex flex-col max-w-[75%]', isOwn && 'items-end')}>
                {/* Username & Role */}
                {!isOwn && (
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold">{message.userName}</span>
                        <Badge
                            className={cn('text-[10px] px-1.5 py-0 h-4', getRoleBadgeColor(message.userRole))}
                        >
                            {message.userRole}
                        </Badge>
                    </div>
                )}

                {/* Message Bubble */}
                <div
                    className={cn(
                        'px-3 py-2 rounded-lg break-words',
                        isOwn
                            ? 'bg-gradient-to-r from-primary to-secondary text-primary-foreground'
                            : 'bg-muted'
                    )}
                >
                    <p className="text-sm">{message.content}</p>
                </div>

                {/* Timestamp */}
                <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                    {formatTime(message.timestamp)}
                    {message.edited && ' (edited)'}
                </span>
            </div>
        </div>
    );
}
